// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React from 'react';
import { motion } from 'framer-motion';
import { Cookie } from 'lucide-react';

export default function CookiePolicyPage() {
  return (
    <div className="min-h-screen bg-[#1C1C1A] py-16 px-4" data-testid="cookie-policy-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-[820px] mx-auto">
        <div className="flex items-center gap-3 mb-3">
          <Cookie className="w-6 h-6 text-[#C9A84C]" />
          <h1 className="text-4xl font-bold text-[#E8E8E4]">Cookie Policy</h1>
        </div>
        <p className="text-sm text-[#888780] mb-10">Ultimo aggiornamento: Maggio 2026 — Versione 2.1</p>
        <div className="space-y-8 text-[#888780] leading-relaxed text-base">

          <section>
            <h2 className="text-xl font-semibold text-[#E8E8E4] mb-3">1. Cosa sono i cookie</h2>
            <p>I cookie sono piccoli file di testo che i siti web salvano sul tuo dispositivo quando li visiti. Servono a far funzionare il sito, ricordare le tue preferenze e — solo con il tuo consenso esplicito — raccogliere dati statistici anonimi.</p>
          </section>

          {/* COOKIE TABLE */}
          <section data-testid="cookie-table-section">
            <h2 className="text-xl font-semibold text-[#E8E8E4] mb-3">2. Tabella completa dei cookie utilizzati</h2>
            <div className="overflow-x-auto rounded-xl" style={{ border: '1px solid rgba(255,255,255,0.06)' }}>
              <table className="w-full text-sm" data-testid="cookie-table">
                <thead>
                  <tr style={{ background: '#252523' }}>
                    <th className="text-left p-3 text-[#E8E8E4] font-semibold">Nome cookie</th>
                    <th className="text-left p-3 text-[#E8E8E4] font-semibold">Categoria</th>
                    <th className="text-left p-3 text-[#E8E8E4] font-semibold">Finalità</th>
                    <th className="text-left p-3 text-[#E8E8E4] font-semibold">Durata</th>
                    <th className="text-left p-3 text-[#E8E8E4] font-semibold">Origine</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-white/[0.06]">
                    <td className="p-3"><code className="text-[#C9A84C]">session_token</code></td>
                    <td className="p-3 text-[#3D7A52]">Tecnico</td>
                    <td className="p-3 text-[#888780]">Autenticazione utente</td>
                    <td className="p-3 text-[#888780]">Sessione</td>
                    <td className="p-3 text-[#888780]">Prima parte (SAPIENS)</td>
                  </tr>
                  <tr className="border-t border-white/[0.06]">
                    <td className="p-3"><code className="text-[#C9A84C]">admin_session</code></td>
                    <td className="p-3 text-[#3D7A52]">Tecnico</td>
                    <td className="p-3 text-[#888780]">Sessione admin panel</td>
                    <td className="p-3 text-[#888780]">Sessione</td>
                    <td className="p-3 text-[#888780]">Prima parte</td>
                  </tr>
                  <tr className="border-t border-white/[0.06]">
                    <td className="p-3"><code className="text-[#C9A84C]">sapiens_cookie_consent</code></td>
                    <td className="p-3 text-[#3D7A52]">Tecnico</td>
                    <td className="p-3 text-[#888780]">Memorizza le tue preferenze cookie</td>
                    <td className="p-3 text-[#888780]">180 giorni</td>
                    <td className="p-3 text-[#888780]">Prima parte</td>
                  </tr>
                  <tr className="border-t border-white/[0.06]">
                    <td className="p-3"><code className="text-[#C9A84C]">sapiens_calc_tab</code></td>
                    <td className="p-3 text-[#3D7A52]">Tecnico</td>
                    <td className="p-3 text-[#888780]">Ricorda l'ultima categoria di calcolatori usata</td>
                    <td className="p-3 text-[#888780]">Persistente</td>
                    <td className="p-3 text-[#888780]">Prima parte</td>
                  </tr>
                  <tr className="border-t border-white/[0.06]">
                    <td className="p-3"><code className="text-[#C9A84C]">_ga</code></td>
                    <td className="p-3 text-[#C9A84C]">Analitico</td>
                    <td className="p-3 text-[#888780]">Identificatore anonimo Google Analytics 4</td>
                    <td className="p-3 text-[#888780]">2 anni</td>
                    <td className="p-3 text-[#888780]">Terza parte (Google LLC, USA)</td>
                  </tr>
                  <tr className="border-t border-white/[0.06]">
                    <td className="p-3"><code className="text-[#C9A84C]">_ga_*</code></td>
                    <td className="p-3 text-[#C9A84C]">Analitico</td>
                    <td className="p-3 text-[#888780]">Stato sessione GA4 (anonimizzato, IP troncato)</td>
                    <td className="p-3 text-[#888780]">2 anni</td>
                    <td className="p-3 text-[#888780]">Terza parte (Google LLC, USA)</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-sm">SAPIENS <strong className="text-[#3D7A52]">non utilizza cookie di profilazione pubblicitaria</strong>. Non vendiamo dati a terzi e non eseguiamo retargeting.</p>
          </section>

          {/* EXTRA-EU TRANSFER */}
          <section data-testid="cookie-extra-eu-section">
            <h2 className="text-xl font-semibold text-[#E8E8E4] mb-3">3. Trasferimenti di dati al di fuori dell'UE</h2>
            <p>I cookie analitici di Google Analytics 4 comportano un trasferimento di dati verso server <strong className="text-[#E8E8E4]">Google LLC negli Stati Uniti</strong>. Questo trasferimento è coperto dalle seguenti tutele previste dal Capo V del Regolamento UE 2016/679 (GDPR):</p>
            <ul className="mt-3 space-y-2 list-disc list-inside text-sm">
              <li><strong className="text-[#E8E8E4]">EU-U.S. Data Privacy Framework</strong> — Decisione di adeguatezza della Commissione UE del 10 luglio 2023 (Google LLC è certificata).</li>
              <li><strong className="text-[#E8E8E4]">Standard Contractual Clauses (SCC)</strong> — Decisione UE 2021/914.</li>
              <li><strong className="text-[#E8E8E4]">IP Anonymization</strong> attivata di default sulla configurazione GA4 SAPIENS.</li>
            </ul>
            <p className="mt-3 text-sm">Per richiedere copia delle SCC scrivi a <a href="mailto:privacy@finsapiens.it" className="text-[#C9A84C]">privacy@finsapiens.it</a>.</p>
          </section>

          {/* BASE GIURIDICA */}
          <section>
            <h2 className="text-xl font-semibold text-[#E8E8E4] mb-3">4. Base giuridica</h2>
            <p>La gestione dei cookie è regolata da:</p>
            <ul className="mt-2 space-y-1 list-disc list-inside text-sm">
              <li>Art. 122 del <strong className="text-[#E8E8E4]">D.Lgs. 196/2003</strong> (Codice Privacy)</li>
              <li>Art. 6 del <strong className="text-[#E8E8E4]">GDPR (Reg. UE 2016/679)</strong></li>
              <li><strong className="text-[#E8E8E4]">Provvedimento Garante Privacy del 10/06/2021</strong> in materia di cookie e altri strumenti di tracciamento</li>
            </ul>
            <p className="mt-3 text-sm">I cookie tecnici sono installati senza consenso (art. 122, co. 1 Cod. Privacy). I cookie analitici GA4 richiedono consenso esplicito ai sensi del Provvedimento Garante.</p>
          </section>

          {/* REVOCA CONSENSO */}
          <section data-testid="cookie-revoke-section">
            <h2 className="text-xl font-semibold text-[#E8E8E4] mb-3">5. Come revocare il consenso</h2>
            <p>Puoi revocare o modificare in qualsiasi momento il consenso ai cookie non tecnici:</p>
            <ul className="mt-3 space-y-2 list-disc list-inside text-sm">
              <li><strong className="text-[#E8E8E4]">Sul sito SAPIENS</strong>: clicca sull'icona <span className="inline-block px-2 py-0.5 rounded" style={{ background: '#252523' }}>🍪</span> in basso a sinistra in qualsiasi pagina per riaprire il banner e modificare le preferenze.</li>
              <li>La revoca è <strong className="text-[#E8E8E4]">immediata</strong>: dopo aver rifiutato i cookie analitici, GA4 viene disattivato e i relativi cookie scadono al massimo entro 24 ore.</li>
            </ul>
          </section>

          {/* BROWSER OPT-OUT */}
          <section data-testid="cookie-browser-optout-section">
            <h2 className="text-xl font-semibold text-[#E8E8E4] mb-3">6. Disattivare i cookie dal browser</h2>
            <p>Puoi anche bloccare o cancellare i cookie direttamente dal tuo browser:</p>
            <ul className="mt-3 space-y-2 list-disc list-inside text-sm">
              <li><strong className="text-[#E8E8E4]">Google Chrome</strong>: <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-[#C9A84C] underline">Impostazioni → Privacy e sicurezza → Cookie</a></li>
              <li><strong className="text-[#E8E8E4]">Mozilla Firefox</strong>: <a href="https://support.mozilla.org/it/kb/Eliminare%20i%20cookie" target="_blank" rel="noopener noreferrer" className="text-[#C9A84C] underline">Impostazioni → Privacy e sicurezza</a></li>
              <li><strong className="text-[#E8E8E4]">Safari</strong>: <a href="https://support.apple.com/it-it/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-[#C9A84C] underline">Preferenze → Privacy</a></li>
              <li><strong className="text-[#E8E8E4]">Microsoft Edge</strong>: <a href="https://support.microsoft.com/it-it/microsoft-edge/eliminare-i-cookie-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="text-[#C9A84C] underline">Impostazioni → Cookie e autorizzazioni sito</a></li>
              <li><strong className="text-[#E8E8E4]">Opt-out specifico Google Analytics</strong>: <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-[#C9A84C] underline">Google Analytics Opt-out Add-on</a></li>
            </ul>
            <p className="mt-3 text-sm">I cookie tecnici non possono essere disattivati senza compromettere il funzionamento del sito (login, preferenze, banner).</p>
          </section>

          {/* DURATA */}
          <section>
            <h2 className="text-xl font-semibold text-[#E8E8E4] mb-3">7. Durata del consenso</h2>
            <p>Le preferenze espresse nel banner hanno validità <strong className="text-[#E8E8E4]">180 giorni</strong>, conformemente al Provvedimento del Garante. Scaduto questo periodo, il banner ti verrà ripresentato.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#E8E8E4] mb-3">8. Contatti</h2>
            <p>Per domande sulla Cookie Policy o sull'uso dei dati: <a href="mailto:privacy@finsapiens.it" className="text-[#C9A84C]">privacy@finsapiens.it</a></p>
            <p className="mt-2 text-sm">Per maggiori informazioni sul trattamento dei dati personali consulta la <a href="/privacy" className="text-[#C9A84C] underline">Privacy Policy</a>.</p>
          </section>
        </div>
      </motion.div>
    </div>
  );
}
